import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class GenericModalService {
  private modalElement: HTMLDialogElement | null = null;
  private modalCountdownIntervalId: number | null = null;

  private formatTimeAsMinutesAndSeconds(seconds: number): string {
    const minutes = String(Math.floor(seconds / 60)).padStart(2, '0');
    const remainingSeconds = String(seconds % 60).padStart(2, '0');
    return `${minutes}:${remainingSeconds}`;
  }

  private buildCountdownMessage(
    baseMessage: string,
    timeInSeconds: number,
  ): string {
    const formattedTime = this.formatTimeAsMinutesAndSeconds(timeInSeconds);
    return `${baseMessage} ${formattedTime}`;
  }

  private clearModalCountdownIntervalId() {
    if (this.modalCountdownIntervalId) {
      clearInterval(this.modalCountdownIntervalId);
    }
  }

  private startCountdownTimer(
    element: HTMLParagraphElement,
    baseMessage: string,
    initialTimeInSeconds: number,
  ): void {
    if (this.modalCountdownIntervalId) {
      clearInterval(this.modalCountdownIntervalId);
    }

    const avoidTimeoutDellay = 1;

    let remainingTime = initialTimeInSeconds - avoidTimeoutDellay;

    const internalId = setInterval(() => {
      const isTimeExpired = remainingTime < 0;

      if (isTimeExpired && this.modalCountdownIntervalId) {
        clearInterval(this.modalCountdownIntervalId);
        this.hideModal();
        return;
      }

      const updatedMessage = this.buildCountdownMessage(
        baseMessage,
        remainingTime,
      );
      element.innerText = updatedMessage;

      remainingTime -= 1;
    }, 1000);

    this.modalCountdownIntervalId = Number(internalId);
  }

  public createModal(props: {
    title?: string;
    description: string;
    expiresInSenconds?: number;
    allowCloseModal?: boolean;
  }): void {
    const { allowCloseModal = false, expiresInSenconds, description } = props;

    if (this.modalElement) return;

    const dialog = this.GenericModalDialogHtmlElement();

    if (props.title) {
      const titleElement = this.GenericModalTitleHtmlElement(props.title);
      dialog.appendChild(titleElement);
    }

    const descriptionMessage = expiresInSenconds
      ? this.buildCountdownMessage(description, expiresInSenconds)
      : description;

    const descriptionElement =
      this.GenericModalDescriptionHtmlElement(descriptionMessage);
    dialog.appendChild(descriptionElement);

    if (expiresInSenconds) {
      this.startCountdownTimer(
        descriptionElement,
        props.description,
        expiresInSenconds,
      );
    }

    if (allowCloseModal) {
      const closeButton = this.GenericModalButtonHtmlElement('Fechar');
      closeButton.onclick = () => this.hideModal();
      dialog.appendChild(closeButton);
    }

    document.body.appendChild(dialog);
    dialog.showModal();
    this.modalElement = dialog;
  }

  public hideModal(): void {
    if (this.modalElement) {
      this.modalElement.close();
      this.modalElement.remove();
      this.modalElement = null;
      this.clearModalCountdownIntervalId();
    }
  }

  private resetHTMlElementStyles(element: HTMLElement): void {
    element.style.all = 'unset';
  }

  private GenericModalButtonHtmlElement(title: string): HTMLButtonElement {
    const button = document.createElement('button');
    this.resetHTMlElementStyles(button);
    button.innerText = title;

    const buttonStyles: Partial<CSSStyleDeclaration> = {
      padding: '0.625rem 1.25rem',
      border: 'none',
      borderRadius: '0.375rem',
      backgroundColor: '#007CBE',
      color: '#FFFFFF',
      fontSize: '0.875rem',
      fontWeight: '500',
      cursor: 'pointer',
      marginTop: '1.5rem',
      alignSelf: 'flex-end',
      transition: 'background-color 0.2s ease-in-out',
    };

    Object.assign(button.style, buttonStyles);

    button.onmouseenter = () => {
      button.style.backgroundColor = '#006BA6';
    };

    button.onmouseleave = () => {
      button.style.backgroundColor = '#007CBE';
    };

    return button;
  }

  private GenericModalDescriptionHtmlElement(
    text: string,
  ): HTMLParagraphElement {
    const description = document.createElement('p');
    this.resetHTMlElementStyles(description);

    const descriptionStyles: Partial<CSSStyleDeclaration> = {
      fontSize: '1rem',
      lineHeight: '1.6',
      textAlign: 'justify',
    };

    description.innerText = text;
    Object.assign(description.style, descriptionStyles);

    return description;
  }

  private GenericModalDialogHtmlElement(): HTMLDialogElement {
    const dialog = document.createElement('dialog');
    dialog.id = 'custom-modal';

    const dialogStyles: Partial<CSSStyleDeclaration> = {
      padding: '2rem',
      border: 'none',
      borderRadius: '0.75rem',
      boxShadow: '0 0.5rem 2rem rgba(0, 0, 0, 0.15)',
      width: '26.25rem',
      maxWidth: 'calc(100vw - 3rem)',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      color: '#1A1A1A',
      backgroundColor: '#FFFFFF',
      textAlign: 'left',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      boxSizing: 'border-box',
    };

    Object.assign(dialog.style, dialogStyles);

    return dialog;
  }

  private GenericModalTitleHtmlElement(text: string): HTMLHeadingElement {
    const title = document.createElement('h2');
    this.resetHTMlElementStyles(title);
    title.innerText = text;

    const titleStyles: Partial<CSSStyleDeclaration> = {
      fontSize: '1.5rem',
      fontWeight: '600',
      margin: '0',
      color: '#1A1A1A',
    };

    Object.assign(title.style, titleStyles);

    return title;
  }
}
